import {
  Button,
  Col,
  Divider,
  Drawer,
  Flex,
  Image,
  Modal,
  Row,
  Space,
  Steps,
  Typography,
  Tooltip,
} from 'antd';
import { ImageContainer } from '../../../../styles/commonStyles';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { CloseAction, QueryType, Routine } from '../../../../types';
import {
  RoutineSigns,
  RoutineSignImage,
  StepSigns,
  StepSignImage,
  StepImageContainer,
} from './style';
import { useTheme } from 'styled-components';
import SuggestionDrawer from '../SuggestionDrawer';
import { useAppDispatch } from '../../../../store';
import {
  closeDiagnoseConversation,
  closeRoutineConversation,
  selectRoutineConversationId,
  switchStepEvent,
} from '../../../../slices/event';
import { EventInput1 } from '../../../../types/events';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getIso7010SignImagePath,
  iso7010SignDescriptions,
  parseSignNames,
} from '../../../../constants/signs';

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const RoutineDrawer = ({
  open,
  onClose,
  routine,
}: {
  routine: Routine;
  open: boolean;
  onClose: (action?: CloseAction) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [routineDescExpanded, setRoutineDescExpanded] = useState(false);

  const checkRoutineDescOverflow = useCallback(() => {
    const el = document.getElementById('routine-desc-text');
    return el ? el.scrollHeight > el.clientHeight : false;
  }, []);

  const [routineDescOverflows, setRoutineDescOverflows] = useState(false);

  useEffect(() => {
    if (routine?.description) {
      const timer = setTimeout(() => {
        setRoutineDescOverflows(checkRoutineDescOverflow());
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [routine?.description, checkRoutineDescOverflow]);
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showSuggestionDrawer, setShowSuggestionDrawer] = useState(false);
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const [totalSpend, setTotalSpend] = useState<string>('');
  const routineConversationId = useSelector(selectRoutineConversationId);
  const sortedRoutineSteps = [...(routine?.routineSteps || [])].sort(
    (a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0)
  );
  const steps = sortedRoutineSteps.map((i) => ({
    title: (
      <>
        <ImageContainer>
          <Image
            src={i?.images?.[0]?.url}
            fallback="/placeholder.png"
            preview={false}
          />
        </ImageContainer>
        <Typography.Paragraph style={{ marginTop: 4 }}>
          {i.name}
        </Typography.Paragraph>
      </>
    ),
  }));

  const isCompleted = currentStep >= steps.length;

  const handleComplete = () => {
    setShowCloseConfirmModal(false);
    onClose(CloseAction.BACK);
    dispatch(
      closeRoutineConversation({
        routineId: routine.routineID,
        status: EventInput1.SUCCESS,
      })
    );
    dispatch(closeDiagnoseConversation({ status: EventInput1.SUCCESS }));
  };

  const handleSuggest = () => {
    setShowSuggestionDrawer(true);
    dispatch(
      closeRoutineConversation({
        routineId: routine.routineID,
        status: EventInput1.FAIL,
      })
    );
  };

  const handleSuggestClose = () => {
    setShowSuggestionDrawer(false);
    if (isCompleted) {
      onClose();
    }
  };

  const handleCloseButtonClick = () => {
    setShowCloseConfirmModal(true);
  };

  const currentRoutineStep = sortedRoutineSteps[currentStep];

  const switchStep = (nextStep: number) => {
    if (nextStep >= steps.length - 1 && startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      setTotalSpend(formatDuration(elapsed));
    }
    const nextStepId = sortedRoutineSteps[nextStep]?.routineStepID || '';
    dispatch(
      switchStepEvent({
        stepId: currentRoutineStep?.routineStepID || '',
        nextStep: nextStepId || 0,
      })
    );
    setCurrentStep(nextStep);
  };

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setShowSuggestionDrawer(false);
      setTotalSpend('');
      startTimeRef.current = null;
    } else if (routine?.routineSteps?.length) {
      startTimeRef.current = Date.now();
    }
  }, [open]);

  useEffect(() => {
    if (routineConversationId && open) {
      dispatch(
        switchStepEvent({
          stepId: 0,
          nextStep: currentRoutineStep?.routineStepID || '',
        })
      );
    }
  }, [routineConversationId, open]);

  return (
    <Drawer
      open={open}
      push={false}
      title={
        <Flex>
          <Flex vertical flex={1} gap={4}>
            <span>
              {t('repair_routine')} - {routine?.name}
            </span>
            <Flex gap={12}>
              <ImageContainer>
                <Image
                  src={routine?.images?.[0]?.url}
                  preview={!!routine?.images?.[0]?.url}
                  fallback="/placeholder.png"
                />
              </ImageContainer>
              <Flex flex={1} gap={8} vertical>
                {routine?.signNames && (
                  <RoutineSigns>
                    {parseSignNames(routine.signNames).map((code) => (
                      <Tooltip
                        key={code}
                        title={`${code} - ${iso7010SignDescriptions[code]}`}
                      >
                        <RoutineSignImage
                          src={getIso7010SignImagePath(code)}
                          alt={code}
                          title={code.toLowerCase()}
                        />
                      </Tooltip>
                    ))}
                  </RoutineSigns>
                )}
                <Typography.Text
                  id="routine-desc-text"
                  style={{
                    fontWeight: 400,
                    display: '-webkit-box',
                    WebkitLineClamp: routineDescExpanded ? undefined : 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: routineDescExpanded ? 'visible' : 'hidden',
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: routine?.description || '',
                    }}
                  ></div>
                </Typography.Text>
                {routineDescOverflows && (
                  <Typography.Link
                    onClick={() => setRoutineDescExpanded((v) => !v)}
                    style={{ fontSize: 12 }}
                  >
                    {routineDescExpanded ? 'Show less' : 'Show more'}
                  </Typography.Link>
                )}
              </Flex>
            </Flex>
          </Flex>
          {/* <Flex align="center" gap={12}>
            <Typography.Text type="secondary">
              Find other solution?
            </Typography.Text>
            <Button type="primary" ghost onClick={handleSuggest}>
              {t('suggest_a_routine')}
            </Button>
          </Flex> */}
        </Flex>
      }
      width="100%"
      onClose={handleCloseButtonClick}
    >
      <Row style={{ height: '100%' }} gutter={24}>
        <Col span={4}>
          <div style={{ height: '100%', overflowY: 'auto' }}>
            <Steps
              direction="vertical"
              style={{
                minHeight: '100%',
                borderRight: '1px solid rgba(5,5,5,0.06)',
              }}
              current={currentStep}
              items={steps}
            />
          </div>
        </Col>
        <Col span={20}>
          {isCompleted ? (
            <Flex
              vertical
              gap={16}
              style={{
                height: '100%',
              }}
              align="center"
            >
              <Typography.Text
                style={{
                  fontSize: 60,
                  color: theme.antd.colorPrimary,
                  marginTop: 50,
                }}
              >
                <SmileOutlined />
              </Typography.Text>
              <Space direction="vertical" size={0}>
                <Typography.Title level={4}>
                  {t('routine_completed')}
                </Typography.Title>
                {totalSpend && (
                  <Typography.Title
                    level={5}
                    style={{ color: theme.antd.colorPrimary, marginTop: 0 }}
                  >
                    {t('duration')}: {totalSpend}
                  </Typography.Title>
                )}
              </Space>
              <Typography.Title level={5}>
                {t('is_issue_resolved')}
              </Typography.Title>
              <Flex gap={32}>
                {/* <Button
                  type="primary"
                  ghost
                  style={{ width: 130 }}
                  onClick={() => setCurrentStep(steps.length - 1)}
                >
                  <ArrowLeftOutlined />
                  Previous Step
                </Button> */}
                <Button
                  type="primary"
                  danger
                  // ghost
                  style={{ width: 200 }}
                  onClick={() => {
                    onClose();
                    dispatch(
                      closeRoutineConversation({
                        routineId: routine.routineID,
                        status: EventInput1.FAIL,
                      })
                    );
                  }}
                >
                  <ArrowLeftOutlined />
                  {t('no_back_to_diagnosis')}
                </Button>
                <Button
                  type="primary"
                  danger
                  ghost
                  style={{ width: 200 }}
                  onClick={() => {
                    dispatch(
                      closeRoutineConversation({
                        routineId: routine.routineID,
                        status: EventInput1.FAIL,
                      })
                    );
                  }}
                >
                  <QuestionCircleOutlined />
                  {t('no_need_help')}
                </Button>
                <Button
                  type="primary"
                  style={{ width: 200 }}
                  onClick={handleComplete}
                >
                  <CheckCircleOutlined /> {t('yes')}
                </Button>
              </Flex>
              <Divider></Divider>
              <Flex vertical align="center" gap={12}>
                <Typography.Text type="secondary">
                  {t('find_other_solution')}
                </Typography.Text>
                <Button type="primary" ghost onClick={handleSuggest}>
                  {t('suggest_a_routine')}
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Flex vertical gap={24} style={{ height: '100%' }}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Step{currentStep + 1}: {currentRoutineStep?.name}
              </Typography.Title>
              <Flex flex={1} gap={24}>
                <Flex vertical flex={1} gap={8}>
                  {currentRoutineStep?.signNames && (
                    <StepSigns>
                      {parseSignNames(currentRoutineStep.signNames).map(
                        (code) => (
                          <Tooltip
                            key={code}
                            title={`${code} - ${iso7010SignDescriptions[code]}`}
                          >
                            <StepSignImage
                              src={getIso7010SignImagePath(code)}
                              alt={code}
                              title={code.toLowerCase()}
                            />
                          </Tooltip>
                        )
                      )}
                    </StepSigns>
                  )}
                  <Typography.Text>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: currentRoutineStep?.description || '',
                      }}
                    ></div>
                  </Typography.Text>
                </Flex>
                <StepImageContainer flex={2}>
                  <Image
                    src={currentRoutineStep?.images?.[0]?.url}
                    preview={!!currentRoutineStep?.images?.[0]?.url}
                    fallback="/placeholder.png"
                    style={{ width: '100%', height: 'auto' }}
                  />
                </StepImageContainer>
              </Flex>
              <Flex style={{ paddingBottom: 24 }}>
                <Button
                  color="primary"
                  variant="filled"
                  style={{ width: 130 }}
                  disabled={currentStep <= 0}
                  onClick={() => switchStep(currentStep - 1)}
                >
                  <ArrowLeftOutlined />
                  Previous Step
                </Button>
                <Flex flex={1} justify="center">
                  <Button type="primary" ghost>
                    <QuestionCircleOutlined />I Need More Help
                  </Button>
                </Flex>
                {currentStep === steps.length - 1 ? (
                  <Button
                    type="primary"
                    style={{ width: 130 }}
                    onClick={() => setCurrentStep(currentStep + 1)}
                  >
                    <CheckCircleOutlined /> Complete
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    variant="filled"
                    style={{ width: 130 }}
                    disabled={currentStep >= steps.length - 1}
                    onClick={() => switchStep(currentStep + 1)}
                  >
                    Next Step
                    <ArrowRightOutlined />
                  </Button>
                )}
              </Flex>
            </Flex>
          )}
        </Col>
      </Row>
      <SuggestionDrawer
        visible={showSuggestionDrawer}
        onClose={handleSuggestClose}
        onConfirm={handleSuggestClose}
        queryType={QueryType.ROUTINE}
      />
      <Modal
        open={showCloseConfirmModal}
        title={t('routine_repair_exit')}
        onCancel={() => {
          setShowCloseConfirmModal(false);
        }}
        footer={
          <>
            <Button type="primary" ghost onClick={handleComplete}>
              Yes, resolved
            </Button>
            <Button
              type="primary"
              danger
              ghost
              onClick={() => {
                setShowCloseConfirmModal(false);
                onClose();
                dispatch(
                  closeRoutineConversation({
                    status: EventInput1.ABANDON,
                  })
                );
              }}
            >
              No, Leave anyway
            </Button>
            <Button
              type="primary"
              onClick={() => setShowCloseConfirmModal(false)}
            >
              Stay here
            </Button>
          </>
        }
      >
        <p style={{ paddingBottom: 20 }}>Is the issue resolved?</p>
      </Modal>
    </Drawer>
  );
};

export default RoutineDrawer;
